import type { FlatRateCoverage, ReportMetadata } from '../types';

/**
 * Ein Eintrag der PROJEKTLISTE — so, wie `loadProjectsFromCloud` ihn liefert.
 *
 * Kein Projekt, sondern ein Auszug: die Spalten aus `PROJECT_COLUMNS_LIST`
 * (queryColumns.ts), von `data` nur `metadata`, dazu die eingebetteten
 * `project_services`. Was die Liste NICHT laedt — `settings`, `items`,
 * `checklists`, Vorankuendigung, Unterlage —, steht hier bewusst nicht drin.
 *
 * Der Typ ist die Uebersetzung des Vorfalls vom 21.10.2026 in den Compiler:
 * vom 23.05. an wurden Listeneintraege gespeichert, als waeren sie volle
 * Projekte, und 14 Projekte verloren dabei alles ausserhalb der Metadaten.
 * Seitdem weist `saveProjectToCloud` einen Listenauszug zur Laufzeit ab
 * (listenAuszug.ts). Mit diesem Typ faellt derselbe Fehler schon beim
 * Uebersetzen auf: `data` ist kein `Report`, und `saveProjectToCloud` nimmt den
 * Auszug nicht an. Wer aus der Liste heraus speichern will, laedt vorher mit
 * `loadProjectById`.
 *
 * Nullbare Spalten stehen als `| null` da, obwohl tsconfig ohne
 * strictNullChecks laeuft: der Typ soll sagen, was die Datenbank liefert, und
 * nicht erst stimmen, wenn der Schalter einmal umgelegt wird.
 *
 * `type` und nicht `interface`: die Listenwege (Realtime, Abgleich,
 * IndexedDB-Cache) fuehren Eintraege als `Record<string, unknown>`, und nur ein
 * Typ-Alias ist dem zuweisbar — ein Interface hat keine implizite
 * Index-Signatur.
 */
export type ListProject = {
    id: string;
    name: string | null;
    updated_at: string | null;
    created_at: string | null;
    user_id: string | null;
    transfer_target_id: string | null;
    project_number: string | null;
    status: string | null;
    customer_id: string | null;
    next_scheduled_date: string | null;
    last_inspection_date: string | null;
    current_inspection_number: number | null;
    last_exported_inspection_number: number | null;
    is_legacy_data_corrected: boolean | null;
    address: string | null;
    client_name: string | null;
    coordinator_name: string | null;
    start_date: string | null;
    end_date: string | null;
    category: string | null;
    subcategory: string | null;
    /** Mutter-Tochter-Verknuepfung (Nr. 71) — die Spalten sind die Wahrheit, siehe projectLink.ts. */
    parent_project_id: string | null;
    linked_billing_mode: string | null;
    linked_billing_history: unknown;
    /** Roh eingebettet; dieselben Zeilen liegen gemappt in `data.metadata.globalData.services`. */
    project_services?: ListProjectServiceRow[];
    data: ListProjectData;
};

/**
 * `data` eines Listeneintrags: nur die Metadaten.
 *
 * Fehlen sie in der Datenbank, setzt der Ladeweg `{ globalData: {} }` ein —
 * der Typ verspricht also kein vollstaendiges `ReportMetadata`, nur dessen
 * Form. Die Kennung `__listenAuszug` setzt `markiereListenAuszug`.
 */
export type ListProjectData = {
    metadata: ReportMetadata;
    __listenAuszug?: true;
};

/** Eine eingebettete Zeile aus `project_services` (`PROJECT_SERVICES_COLUMNS`). */
export type ListProjectServiceRow = {
    id: string;
    lv_position: string | null;
    title: string | null;
    quantity_contracted: number | null;
    quantity_used: number | null;
    quantity_billed: number | null;
    unit_price: number | null;
    assignment: string | null;
    unit: string | null;
    is_optional: boolean | null;
    status: string | null;
    category: string | null;
    client_lv_position: string | null;
    min_hours: number | null;
    quantity_waived: number | null;
    waived_reason: string | null;
    waived_at: string | null;
    waived_by: string | null;
    waived_entry_ids: string[] | null;
    /** MONATSPAUSCHALE (Nr. 53) — JSON, im Ladeweg zu `flatRate` gemappt. */
    flat_rate: FlatRateCoverage | null;
    transferred_from: unknown;
    /** JSON — gelesen ueber projectLv.bestandteilFelderAusZeile. */
    bestandteile: unknown;
    /** Gate der Paketposition, von der Datenbank gepflegt (Nr. 166). */
    bestandteile_offen: number | string | null;
    stunden_kontingent: unknown;
    stunden_erfasst: unknown;
};

/**
 * Die eine Stelle, an der eine GEBAUTE Listenzeile ihren Typ bekommt.
 *
 * Fuer die Wege neben `loadProjectsFromCloud`, die Zeilen roh als
 * `Record<string, unknown>` fuehren (Realtime, Abgleich, IndexedDB-Cache).
 * Aufrufen erst NACH `bildeListenEintrag` bzw. dem Kennzeichnen als
 * Listenauszug — vorher ist die Zeile noch keine Listenform. Die Pruefung, dass
 * die Spalten stimmen, leistet die Abfrage (`projektListeSelect`), nicht
 * dieser Cast.
 */
export const alsListProject = (zeile: Record<string, unknown>): ListProject => zeile as ListProject;
